// ==============================================
// Invoices Page - User Invoice History
// ==============================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import type { Invoice } from '../../types/subscription';
import clsx from 'clsx';

function formatCurrency(cents: number, currency: string = 'NZD'): string {
  return (cents / 100).toLocaleString('en-NZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-NZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getStatusBadge(status: Invoice['status']) {
  switch (status) {
    case 'paid':
      return {
        icon: CheckCircleIcon,
        text: 'Paid',
        className: 'bg-green-500/10 text-green-400 border-green-500/30',
      };
    case 'open':
      return {
        icon: ClockIcon,
        text: 'Open',
        className: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
      };
    case 'void':
      return {
        icon: XCircleIcon,
        text: 'Void',
        className: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
      };
    case 'uncollectible':
      return {
        icon: XCircleIcon,
        text: 'Uncollectible',
        className: 'bg-red-500/10 text-red-400 border-red-500/30',
      };
    default:
      return {
        icon: DocumentTextIcon,
        text: status,
        className: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
      };
  }
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const [showDetails, setShowDetails] = useState(false);
  const statusBadge = getStatusBadge(invoice.status);
  const StatusIcon = statusBadge.icon;

  return (
    <>
      <tr
        className="border-b border-slate-700/50 hover:bg-slate-800/30 cursor-pointer transition-colors"
        onClick={() => setShowDetails(!showDetails)}
      >
        <td className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
              <DocumentTextIcon className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="font-medium text-white">{invoice.invoice_number || '-'}</p>
              <p className="text-sm text-slate-400">{formatDate(invoice.invoice_date || invoice.created_at)}</p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4">
          <p className="text-slate-300 text-sm line-clamp-1">
            {invoice.description || 'Subscription payment'}
          </p>
        </td>
        <td className="px-4 py-4">
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border',
              statusBadge.className
            )}
          >
            <StatusIcon className="w-3.5 h-3.5" />
            {statusBadge.text}
          </span>
        </td>
        <td className="px-4 py-4 text-right">
          <p className="font-semibold text-white">{formatCurrency(invoice.total_cents, invoice.currency)}</p>
          {invoice.gst_rate && (
            <p className="text-xs text-slate-500">incl. GST</p>
          )}
        </td>
        <td className="px-4 py-4 text-right">
          <div className="flex items-center justify-end gap-2">
            {invoice.hosted_invoice_url && (
              <a
                href={invoice.hosted_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
                title="View Invoice"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
              </a>
            )}
            {invoice.invoice_pdf_url && (
              <a
                href={invoice.invoice_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
                title="Download Invoice"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
              </a>
            )}
          </div>
        </td>
      </tr>

      {/* Invoice Details Expanded Row */}
      {showDetails && (
        <tr className="border-b border-slate-700/50 bg-slate-800/50">
          <td colSpan={5} className="px-4 py-4">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column - Invoice Details */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-white">Invoice Details</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Invoice Number</p>
                    <p className="text-white font-mono">{invoice.invoice_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Invoice Date</p>
                    <p className="text-white">{formatDate(invoice.invoice_date || invoice.created_at)}</p>
                  </div>
                  {invoice.due_date && (
                    <div>
                      <p className="text-slate-500">Due Date</p>
                      <p className="text-white">{formatDate(invoice.due_date)}</p>
                    </div>
                  )}
                  {invoice.paid_at && (
                    <div>
                      <p className="text-slate-500">Paid Date</p>
                      <p className="text-white">{formatDate(invoice.paid_at)}</p>
                    </div>
                  )}
                  {invoice.receipt_number && (
                    <div>
                      <p className="text-slate-500">Receipt Number</p>
                      <p className="text-white font-mono">{invoice.receipt_number}</p>
                    </div>
                  )}
                  {invoice.payment_method && (
                    <div>
                      <p className="text-slate-500">Payment Method</p>
                      <p className="text-white capitalize">{invoice.payment_method}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Amounts */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-white">Amount Details</h4>

                {/* Line Items */}
                {invoice.line_items && invoice.line_items.length > 0 && (
                  <div className="space-y-2">
                    {invoice.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-400">
                          {item.description} {item.quantity > 1 && `x${item.quantity}`}
                        </span>
                        <span className="text-white">{formatCurrency(item.total_cents, invoice.currency)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="border-t border-slate-600 pt-3 space-y-2">
                  {invoice.gst_rate && invoice.subtotal_excl_gst_cents !== null && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Subtotal (excl. GST)</span>
                        <span className="text-white">
                          {formatCurrency(invoice.subtotal_excl_gst_cents, invoice.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">GST ({invoice.gst_rate}%)</span>
                        <span className="text-white">{formatCurrency(invoice.tax_cents, invoice.currency)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span className="text-white">Total{invoice.gst_rate ? ' (incl. GST)' : ''}</span>
                    <span className="text-cyan-400">{formatCurrency(invoice.total_cents, invoice.currency)}</span>
                  </div>
                  {invoice.amount_paid_cents > 0 && invoice.amount_paid_cents !== invoice.total_cents && (
                    <div className="flex justify-between text-sm text-green-400">
                      <span>Amount Paid</span>
                      <span>{formatCurrency(invoice.amount_paid_cents, invoice.currency)}</span>
                    </div>
                  )}
                </div>

                {/* GST Info */}
                {invoice.company_gst_number && (
                  <div className="text-xs text-slate-500 pt-2 border-t border-slate-700">
                    <p>Supplier GST: {invoice.company_gst_number}</p>
                    {invoice.company_name && <p>From: {invoice.company_name}</p>}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function InvoicesPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoices', page, statusFilter],
    queryFn: () => api.getMyInvoices({ page, limit: 10, status: statusFilter }),
  });

  const invoices = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <p className="text-slate-400">View and download your invoices and receipts</p>
        </div>

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-400">Filter:</span>
          <select
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter(e.target.value || undefined);
              setPage(1);
            }}
            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
          >
            <option value="">All Invoices</option>
            <option value="paid">Paid</option>
            <option value="open">Open</option>
            <option value="void">Void</option>
            <option value="uncollectible">Uncollectible</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <XCircleIcon className="w-12 h-12 mb-2 text-red-400" />
            <p>Failed to load invoices</p>
          </div>
        ) : invoices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <DocumentTextIcon className="w-12 h-12 mb-2 text-slate-600" />
            <p>No invoices yet</p>
            <p className="text-sm">Your invoices will appear here after your first payment</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => (
                <InvoiceRow key={invoice.id} invoice={invoice} />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} invoices
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="p-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="text-sm text-white px-3">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
              disabled={page === pagination.totalPages}
              className="p-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* NZ Compliance Note */}
      <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
        <p className="text-xs text-slate-500">
          All invoices are GST compliant for New Zealand tax purposes. Prices shown include GST at the
          applicable rate for NZ customers. For international customers, GST does not apply.
        </p>
      </div>
    </div>
  );
}

export default InvoicesPage;
